import { NgClass, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { isNil } from 'lodash-es';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  interval,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  HasPermissionDirective,
  triggerIfSlow,
  UserService,
  WebSocketService,
} from '@ipg/ngx-frontend-toolset';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { DeviceTransferDialogComponent } from '../../components/device-transfer-dialog/device-transfer-dialog.component';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';

import { GlobalLoadersService } from '../../global-loaders.service';
import {
  DEVICE_TYPE_LABELS,
  Device,
  DeviceConnectionPayload,
  DeviceTelemetryPayload,
  DeviceWebSocketPayload,
  isIvyFamilyType,
} from '../../models/devices.model';
import { User } from '../../models/user.model';
import { FormatIntervalPipe } from '../../pipes/format-interval.pipe';
import { MapFromPipe } from '../../pipes/map-from.pipe';
import { DevicesService } from '../../services/devices.service';
import { TableLimitService } from '../../services/table-limit.service';
import { UnitService } from '../units/services/unit.service';
import { UnitResponseModel } from '../units/units.model';

@Component({
  selector: 'app-devices-robots',
  host: { class: 'flex flex-col flex-1 min-h-0' },
  imports: [
    MatIcon,
    MatIconButton,
    MatTableModule,
    MatTooltip,
    MatPaginator,
    MatSort,
    MatSortHeader,
    DataTableContainerComponent,
    MapFromPipe,
    TitleCasePipe,
    NgClass,
    RouterLink,
    FormatIntervalPipe,
    MatInput,
    MatFormField,
    MatPrefix,
    MatSuffix,
    FormsModule,
    MatChipListbox,
    MatChipOption,
    HasPermissionDirective,
  ],
  templateUrl: './devices-robots.component.html',
  styleUrl: './devices.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevicesRobotsComponent {
  private static readonly LOADER_CALLER_ID = 'devices_robots_loader_caller';
  private static readonly UNITS_LOADER_CALLER_ID = 'devices_robots_units_loader_caller';
  private static readonly UNITS_TRANSFER_CALLER_ID = 'devices_robots_units_transfer_caller';

  private readonly _userService = inject<UserService<User>>(UserService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _dialog = inject(DialogService);
  private readonly _tableLimit = inject(TableLimitService);
  private readonly _devices = inject(DevicesService);
  private readonly _wsService = inject(WebSocketService);
  private readonly _units = inject(UnitService);

  private readonly _destroyRef = inject(DestroyRef);

  private readonly _fetch$ = new Subject<void>();

  private readonly _keywordInput$ = new Subject<string | null>();
  private readonly _debouncedKeyword$ = this._keywordInput$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this._query.update((currentState) => ({ ...currentState, page: 1 })))
  );

  protected readonly keyword = signal<string | null>(null);
  protected readonly includeDescendants = signal<boolean>(false);

  private readonly _hasLoadedData = signal<boolean>(false);

  private readonly _query = signal({
    page: 1,
    limit: this._tableLimit.getLimit(),
  });

  private readonly _sort = signal({
    sortBy: 'label',
    sortOrder: 'asc',
  });

  private readonly _includeDescendants$ = toObservable(this.includeDescendants).pipe(
    tap(() => this._query.update((s) => ({ ...s, page: 1 })))
  );

  private readonly _params$ = combineLatest({
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    keyword: this._debouncedKeyword$.pipe(startWith(this.keyword())),
    includeDescendants: this._includeDescendants$,
    __fetch: this._fetch$.pipe(startWith(undefined)),
  }).pipe(
    map(({ query, sort, keyword, includeDescendants }) => ({
      ...query,
      ...sort,
      category: 'robot',
      ...(keyword ? { q: keyword.trim() } : null),
      ...(includeDescendants ? { includeDescendants: true } : null),
    })),
    debounceTime(0)
  );

  private readonly _personalized_units$ = this._units.getByUser().pipe(
    map((response) => response.filter((value) => !value.deleteAt)),
    tap({
      error: () =>
        this._dialog.alert({
          title: `Foutmelding`,
          message: `Er is iets misgegaan bij het ophalen van de apparaten. Probeer het later nog eens`,
          confirmText: `OK`,
        }),
    }),
    takeUntilDestroyed(this._destroyRef),
    triggerIfSlow(() =>
      this._globalLoaders.setActive(
        DevicesRobotsComponent.UNITS_LOADER_CALLER_ID,
        HeaderComponent.LOADER_ID
      )
    ),
    finalize(() =>
      this._globalLoaders.setInactive(
        DevicesRobotsComponent.UNITS_LOADER_CALLER_ID,
        HeaderComponent.LOADER_ID
      )
    )
  );

  private readonly _paginatedDevices$ = this._params$.pipe(
    switchMap((params) =>
      this._devices
        .getAll({
          params,
        })
        .pipe(
          tap({
            error: () =>
              this._dialog.alert({
                title: `Foutmelding`,
                message: `Er is iets misgegaan bij het ophalen van de apparaten. Probeer het later nog eens`,
                confirmText: `OK`,
              }),
          }),
          takeUntilDestroyed(this._destroyRef),
          triggerIfSlow(() =>
            this._globalLoaders.setActive(
              DevicesRobotsComponent.LOADER_CALLER_ID,
              HeaderComponent.LOADER_ID
            )
          ),
          finalize(() =>
            this._globalLoaders.setInactive(
              DevicesRobotsComponent.LOADER_CALLER_ID,
              HeaderComponent.LOADER_ID
            )
          )
        )
    ),
    tap(() => this._hasLoadedData.set(true)),
    tap(({ meta }) => this._setPaginator({ page: meta.page, total: meta.total }))
  );

  private readonly _devices$ = this._paginatedDevices$.pipe(map(({ items }) => items));

  private readonly _paginator = viewChild(MatPaginator);

  private readonly _globalLoaders = inject(GlobalLoadersService);

  protected readonly router = inject(Router);

  protected readonly robotTypeMap = DEVICE_TYPE_LABELS;

  protected readonly dataSource = signal<Device[]>([]);

  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  protected readonly pageSize = computed(() => this._query().limit);

  protected readonly isSearchDisabled = computed(() => !this._hasLoadedData());

  private static readonly BASE_COLUMNS = [
    'idAlias',
    'label',
    'type',
    'unit',
    'attachedTo',
    'batteryLevel',
    'status',
    'duration',
    'actions',
  ];

  protected readonly displayedColumns = computed(() =>
    DevicesRobotsComponent.BASE_COLUMNS.filter(
      (col) => col !== 'unit' || this.includeDescendants()
    )
  );

  protected readonly currentTime = signal(Date.now());
  protected readonly currentUnitId = computed(
    () => this._userService.data()?.unitId ?? null
  );
  protected readonly unitsByUser = signal<UnitResponseModel[]>([]);
  protected readonly unitMap = computed(
    () => new Map(this.unitsByUser().map((u) => [u._id, u.name]))
  );
  protected readonly dialogComponent = inject(MatDialog);

  constructor() {
    this._devices$.pipe(takeUntilDestroyed()).subscribe((devs) => {
      this.dataSource.set(devs);
    });

    this._personalized_units$.pipe(takeUntilDestroyed()).subscribe((units) => {
      this.unitsByUser.set(units);
    });

    interval(1000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.currentTime.set(Date.now());
      });

    this._wsService
      .connect<DeviceWebSocketPayload>('/event-stream', {
        requireAuth: true,
        topics: ['device/#'],
      })
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (connection) => {
          console.log('WebSocket connected for device updates');

          connection.events$.subscribe((event) => {
            console.log('Device WebSocket Event:', event);
            this._handleDeviceWebSocketEvent(event);
          });
        },
        error: (error) => console.error('WebSocket connection failed:', error),
      });
  }

  private _handleDeviceWebSocketEvent(event: {
    eventType: string;
    payload: DeviceWebSocketPayload;
  }): void {
    const { eventType, payload } = event;

    if (
      eventType === 'device.connection.open' ||
      eventType === 'device.connection.closed'
    ) {
      this._handleConnectionEvent(payload as DeviceConnectionPayload);
    } else if (eventType === 'device.telemetry') {
      this._handleTelemetryEvent(payload as DeviceTelemetryPayload);
    }
  }

  private _handleConnectionEvent(payload: DeviceConnectionPayload): void {
    this.dataSource.update((currentDevices) => {
      const deviceIndex = currentDevices.findIndex(
        (device) => device._id === payload.deviceId
      );

      if (deviceIndex === -1) {
        console.warn('Device not found in current list:', payload.deviceId);
        return currentDevices;
      }

      const updatedDevices = [...currentDevices];
      const device = { ...updatedDevices[deviceIndex] };

      if (device.latestSession) {
        device.latestSession = {
          ...device.latestSession,
          status: payload.status,
          lastSeenAt: payload.timestamp,
          closedAt: payload.status === 'closed' ? payload.timestamp : null,
          createdAt: payload.connectedAt,
        };
      } else if (payload.status === 'open') {
        device.latestSession = {
          id: payload.sessionId,
          createdAt: payload.connectedAt,
          updatedAt: payload.timestamp,
          closedAt: null,
          lastSeenAt: payload.timestamp,
          duration: 0,
          deviceId: payload.deviceId,
          status: 'open',
        };
      }

      updatedDevices[deviceIndex] = device;
      return updatedDevices;
    });
  }

  private _handleTelemetryEvent(payload: DeviceTelemetryPayload): void {
    this.dataSource.update((currentDevices) => {
      const deviceIndex = currentDevices.findIndex(
        (device) => device._id === payload.deviceId
      );

      if (deviceIndex === -1) {
        console.warn('Device not found in current list:', payload.deviceId);
        return currentDevices;
      }

      const updatedDevices = [...currentDevices];
      const device = { ...updatedDevices[deviceIndex] };

      if (isIvyFamilyType(device.type) && 'telemetry' in device.metadata) {
        device.metadata = {
          ...device.metadata,
          telemetry: {
            ...device.metadata.telemetry,
            ...payload.telemetry,
          },
        };
      } else if (
        (device.type === 'ozzy' || device.type === 'ozzyAndroid') &&
        'batteryLevel' in device.metadata
      ) {
        if (payload.telemetry.batteryLevel !== undefined) {
          device.metadata = {
            ...device.metadata,
            batteryLevel: payload.telemetry.batteryLevel,
          };
        }
      }

      if (device.latestSession && device.latestSession.status === 'open') {
        device.latestSession = {
          ...device.latestSession,
          lastSeenAt: payload.timestamp,
        };
      }

      updatedDevices[deviceIndex] = device;
      return updatedDevices;
    });
  }

  protected activateUnit(unitId: string) {
    const unitName = this.unitMap().get(unitId) ?? unitId;

    this._units
      .activate(unitId)
      .pipe(
        catchError(() => {
          this._dialog.alert({
            title: 'Activeren mislukt',
            message:
              'Er is iets misgegaan bij het activeren van de unit. Probeer het later nog eens.',
            confirmText: 'OK',
          });

          return EMPTY;
        }),
        switchMap(() => this._userService.fetch())
      )
      .subscribe(() => {
        this._snackBar.open(`Unit ${unitName} geactiveerd`, 'OK', {
          duration: 3000,
        });
        this._fetch$.next();
      });
  }

  protected openUnitTransfer(device: Device) {
    this._dialog.open(
      DeviceTransferDialogComponent,
      {
        device: device,
        units: this.unitsByUser(),
        onConfirm: (unit: UnitResponseModel) =>
          this._devices
            .transfer(device._id, unit._id)
            .pipe(tap(() => this._fetch$.next())),
        resetDataHandler: () => this._fetch$.next(),
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }

  protected handleEditLabel(device: Device) {
    this._dialog.prompt(
      {
        title: 'Naam wijzigen',
        message: '',
        confirmText: 'Opslaan',
        cancelText: 'Annuleren',
        input: {
          mode: 'input',
          defaultValue: device.label ?? '',
          placeholder: 'Naam apparaat',
          validators: {
            required: {
              validator: Validators.required,
              errorMessage: 'Vul een apparaatnaam in',
            },
            maxlength: {
              validator: Validators.maxLength(100),
              errorMessage: (args) => {
                const errors = args as ValidationErrors;

                return `Maximaal ${errors['requiredLength']} karakters`;
              },
            },
          },
        },
        onConfirm: (result) =>
          of(result).pipe(
            filter(
              (result): result is string => !isNil(result) && result.trim().length > 0
            ),
            switchMap((label) =>
              this._devices.update(device._id, { label: label.trim() }).pipe(
                tap(() => this._fetch$.next()),
                catchError(() => {
                  this._dialog.alert({
                    title: 'Foutmelding',
                    message:
                      'Er is iets fout gegaan met het bewerken van de apparaatnaam. Probeer het later nog eens.',
                    confirmText: 'Begrepen',
                  });

                  return EMPTY;
                })
              )
            )
          ),
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this._query.update((currentState) => ({
      ...currentState,
      page: pageIndex + 1,
      limit: pageSize,
    }));
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.update((currentState) => ({
      ...currentState,
      sortBy: active,
      sortOrder: direction,
    }));
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim());
    this._keywordInput$.next(value.trim());
  }

  clearSearch() {
    this.keyword.set(null);
    this._keywordInput$.next(null);
  }

  private _setPaginator({ page, total }: { page: number; total: number }) {
    const paginator = this._paginator();

    if (!paginator) {
      return;
    }

    paginator.pageIndex = page - 1;
    paginator.length = total;
  }
}
